/**
 * InboxItemRow — Conversazioni hub row.
 *
 * June 2026 refactor: thin domain wrapper around the canonical
 * :class:`ListItemRow` primitive (in `@ando/ui`). All chrome —
 * stagger animation, hover transitions, selection accent, bg ramp,
 * checkbox-on-hover, hover-revealed actions cluster — lives in the
 * primitive. This file only specialises the SLOTS:
 *
 *   - avatar  → :class:`SenderAvatar` with deterministic per-sender tone
 *   - eyebrow → ``CHANNEL · sender · +N`` (PEC tint or chat glyph)
 *   - title   → subject (bold when ``is_unread``)
 *   - preview → AI summary inline when unread + provider, else snippet
 *   - pills   → Indicizzata + Allegato
 *   - actions → Star / Read / Snooze / Archive / Reply hover cluster
 *   - trailing→ Star glyph + time + unread dot
 */

import { useLingui } from "@lingui/react/macro";
import {
  Archive,
  Clock,
  CornerUpLeft,
  Mail,
  MailOpen,
  MessageSquare,
  Paperclip,
  Sparkles,
  Star,
} from "lucide-react";
import type * as React from "react";
import { useEffect, useRef, useState } from "react";

import type { InboxChannel, InboxItem } from "@ando/core";
import { ListItemActionButton, ListItemEyebrow, ListItemRow, SenderAvatar } from "@ando/ui";

import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import { SnoozePopover } from "@/features/workspace/inbox/components/SnoozePopover";
import { senderTone } from "@/features/workspace/inbox/utils/senderTone";

const CHANNEL_LABEL: Record<InboxChannel, string> = {
  email: "Email",
  slack: "Slack",
  teams: "Teams",
  google_chat: "Google Chat",
  pec: "PEC · SDI",
};

const CHAT_CHANNELS: ReadonlySet<InboxChannel> = new Set(["slack", "teams", "google_chat"]);

export interface InboxItemRowProps {
  readonly item: InboxItem;
  readonly index: number;
  readonly onClick: (item: InboxItem) => void;
  /** Hover/focus prefetch — warms the detail (email thread) the click opens. */
  readonly onHoverPrefetch?: (item: InboxItem) => void;
  readonly isActive?: boolean;
  readonly selected?: boolean;
  readonly selectionActive?: boolean;
  readonly onToggleSelect?: (item: InboxItem) => void;
  readonly onToggleRead?: (item: InboxItem) => void;
  readonly onArchive?: (item: InboxItem) => void;
  readonly onSnooze?: (item: InboxItem, epochMs: number) => void;
  readonly onToggleStar?: (item: InboxItem) => void;
  readonly onReply?: (item: InboxItem) => void;
  readonly summaryProvider?: (messageId: string) => string | null;
  readonly threadCount?: number;
  readonly isStarred?: boolean;
  readonly isIngested?: boolean;
  /** @deprecated — accentColor border removed in May 2026 redesign. */
  readonly accentColor?: string;
}

export const InboxItemRow: React.FC<InboxItemRowProps> = ({
  item,
  index,
  onClick,
  onHoverPrefetch,
  isActive = false,
  selected = false,
  selectionActive = false,
  onToggleSelect,
  onToggleRead,
  onArchive,
  onSnooze,
  onToggleStar,
  onReply,
  summaryProvider,
  threadCount,
  isStarred = false,
  isIngested = false,
}) => {
  const { t } = useLingui();
  const isPec = item.channel === "pec";
  const isChat = CHAT_CHANNELS.has(item.channel);
  // PEC overrides the per-sender tint — certified mail = brand-orange.
  const tone = isPec
    ? { bg: "bg-ando-orange-soft", fg: "text-ando-orange" }
    : senderTone(item.sender_display);
  const time = formatRelativeTime(item.received_at);

  // ── AI summary lazy IO ───────────────────────────────────────────────
  const rowRef = useRef<HTMLLIElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = rowRef.current;
    if (!el || !summaryProvider || !item.is_unread) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin: "150px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [summaryProvider, item.is_unread]);

  const aiSummary =
    inView && summaryProvider && item.is_unread ? summaryProvider(item.message_id) : null;

  // ── Slot wiring ──────────────────────────────────────────────────────
  const avatar = (
    <SenderAvatar
      sender={item.sender_display}
      toneBg={tone.bg}
      toneFg={tone.fg}
      size={28}
    />
  );

  const eyebrow = (
    <>
      <ListItemEyebrow
        icon={
          isChat ? (
            <MessageSquare size={9} strokeWidth={2} aria-hidden className="shrink-0" />
          ) : null
        }
        tone={isPec ? "branded" : "neutral"}
      >
        {CHANNEL_LABEL[item.channel]}
      </ListItemEyebrow>
      {item.sender_display ? (
        <span className="truncate text-[10px] text-ink-3">{item.sender_display}</span>
      ) : null}
      {threadCount && threadCount > 1 ? (
        <span
          aria-label={t`Thread con ${threadCount} messaggi`}
          className="inline-flex shrink-0 items-center rounded-full bg-black/[0.06] px-1.5 py-0.5 font-medium text-[9.5px] text-ink-2 tabular-nums"
        >
          +{threadCount - 1}
        </span>
      ) : null}
    </>
  );

  const trailing = (
    // Fades out on hover so the hover action cluster reads in its place.
    <span className="flex items-center gap-1.5 transition-opacity duration-fast group-focus-within:opacity-0 group-hover:opacity-0">
      {isStarred ? (
        <Star
          size={11}
          strokeWidth={0}
          aria-label={t`Stellata`}
          className="fill-amber-500 text-amber-500"
        />
      ) : null}
      <span className="text-[10px] text-ink-3 tabular-nums">{time}</span>
      {item.is_unread ? (
        <span
          aria-label={t`Non letto`}
          className="inline-block h-1.5 w-1.5 rounded-full bg-ando-orange"
        />
      ) : null}
    </span>
  );

  const previewNode = aiSummary ? (
    <span className="flex items-start gap-1.5 text-ink-2">
      <Sparkles
        size={10}
        strokeWidth={2}
        aria-hidden
        className="mt-0.5 shrink-0 text-ando-orange"
      />
      <span className="line-clamp-2">{aiSummary}</span>
    </span>
  ) : item.preview ? (
    item.preview
  ) : null;

  const pills =
    item.has_attachments || isIngested ? (
      <>
        {item.has_attachments ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-ink-4">
            <Paperclip size={11} strokeWidth={2} aria-hidden />
            {t`Con allegati`}
          </span>
        ) : null}
        {isIngested ? (
          <span className="inline-flex items-center rounded-full border border-hairline bg-ink-1/[0.05] px-1.5 py-0.5 font-medium text-[10px] text-ink-3 tracking-[0.08em]">
            {t`Indicizzata`}
          </span>
        ) : null}
      </>
    ) : null;

  const actions =
    onToggleStar || onToggleRead || onSnooze || onArchive || onReply ? (
      <>
        {onToggleStar ? (
          <ListItemActionButton
            className={isStarred ? "text-amber-500 hover:text-amber-500" : "hover:text-amber-500"}
            label={isStarred ? t`Rimuovi stella` : t`Aggiungi stella`}
            icon={
              <Star
                size={12}
                strokeWidth={2}
                aria-hidden
                className={isStarred ? "fill-amber-500" : undefined}
              />
            }
            onClick={() => onToggleStar(item)}
          />
        ) : null}
        {onToggleRead ? (
          <ListItemActionButton
            label={item.is_unread ? t`Segna come letto` : t`Segna come non letto`}
            icon={
              item.is_unread ? (
                <MailOpen size={12} strokeWidth={2} aria-hidden />
              ) : (
                <Mail size={12} strokeWidth={2} aria-hidden />
              )
            }
            onClick={() => onToggleRead(item)}
          />
        ) : null}
        {onSnooze ? (
          <SnoozePopover onPick={(epochMs) => onSnooze(item, epochMs)}>
            <ListItemActionButton
              label={t`Snooze`}
              icon={<Clock size={12} strokeWidth={2} aria-hidden />}
              onClick={() => {
                /* popover-handled */
              }}
            />
          </SnoozePopover>
        ) : null}
        {onArchive ? (
          <ListItemActionButton
            className="hover:bg-red-500/10 hover:text-red-600"
            label={t`Archivia`}
            icon={<Archive size={12} strokeWidth={2} aria-hidden />}
            onClick={() => onArchive(item)}
          />
        ) : null}
        {onReply ? (
          <ListItemActionButton
            label={t`Rispondi`}
            icon={<CornerUpLeft size={12} strokeWidth={2} aria-hidden />}
            onClick={() => onReply(item)}
          />
        ) : null}
      </>
    ) : null;

  return (
    <ListItemRow
      ref={rowRef}
      itemId={item.message_id}
      dataMessageId={item.message_id}
      index={index}
      avatar={avatar}
      eyebrow={eyebrow}
      title={item.subject || t`(senza oggetto)`}
      preview={previewNode}
      pills={pills}
      actions={actions}
      trailing={trailing}
      onClick={() => onClick(item)}
      onPointerEnter={onHoverPrefetch ? () => onHoverPrefetch(item) : undefined}
      onToggleSelect={onToggleSelect ? () => onToggleSelect(item) : undefined}
      selected={selected}
      selectionActive={selectionActive}
      highlighted={item.is_unread}
      isActive={isActive}
      checkboxLabel={t`Seleziona ${item.subject || item.sender_display || t`messaggio`}`}
    />
  );
};
InboxItemRow.displayName = "InboxItemRow";
